import React, {useContext, useEffect, useMemo, useState} from 'react';
import {Location} from 'history';
import {useLocation, useNavigate} from 'react-router-dom';
import {Col, Nav, NavItem, NavLink, Row, TabContent, TabPane} from 'reactstrap';
import {useFormContext} from 'react-hook-form';
import {IFormFieldConfig, IFormTabConfig, IFormTabFieldConfig} from '@src/@interfaces/components/Form/IFormConfig';
import {
    Fields as FieldsProps,
    FormContext,
    FormCustomField,
    FormFieldAvailableTypes,
    FormFieldsProps,
    FormProps
} from '@src/@types/components/Form';
import RenderIf from '@src/components/RenderIf/RenderIf';
import Translate from '@src/components/Translate/Translate';
import classnames from '@src/helpers/classnames';
import FormField from './FormField';
import {IEmptyModel} from '@src/@interfaces/IEmptyModel';
import {FormTabsContext} from '@src/contexts/FormTabsContext';
import usePermissions from '@src/hooks/usePermissions';
import DefaultTabContentWrapper from '@src/components/Form/DefaultTabContentWrapper';
import DefaultTabControlWrapper from '@src/components/Form/DefaultTabControlWrapper';
import NowLoadingMessage from '@src/components/NowLoadingMessage/NowLoadingMessage';

type WithRequirements<Base, Model extends IEmptyModel = IEmptyModel> = {
    model?: Model,
    formName?: string,
    modelOptions?: Record<string, any>,
    labelL10NPrefix?: string | null,
    customFields?: FormProps<Model>['customFields'],
    customTabsContentWrappers?: FormProps<Model>['customTabsContentWrappers'],
    customTabsControlWrappers?: FormProps<Model>['customTabsControlWrappers'],
} & Base;

const errorsBagIsNotEmpty = (bag?: Record<any, any> | any[]): boolean => {
    if (!bag) {
        return false;
    }

    if ('message' in bag && bag.message != null) {
        return true;
    }

    if (Array.isArray(bag)) {
        return bag.some(errorsBagIsNotEmpty);
    }

    if (typeof bag === 'object') {
        return errorsBagIsNotEmpty(Object.values(bag));
    }

    return false;
};

export function Fields<Model extends IEmptyModel>(props: WithRequirements<{
    fields: FieldsProps<Model, IFormFieldConfig | IFormTabFieldConfig>,
    context?: FormContext,
    tabName?: string | number,
}, Model>) {
    const tabsContext = useContext(FormTabsContext);
    const {canAny} = usePermissions();
    const {formState: {errors}} = useFormContext<Model>();
    const tabName = props.tabName;
    const originalFields = props.fields;
    const formContext = props.context;
    const customFields = props.customFields;

    const [fields, keys] = useMemo(() => {
        const result: Record<string, IFormFieldConfig | IFormTabFieldConfig> = {};
        for (const [key, field] of Object.entries(originalFields)) {
            const config = {...field, ...(customFields?.[key]?.config ?? {})};
            const permissions = config.permissions;
            if (
                    (!config.context || !formContext || config.context === formContext) &&
                    (!permissions || canAny(permissions))
            ) {
                result[key] = config;
            }
        }

        return [result, Object.keys(result)];
    }, [originalFields, customFields, formContext, canAny]);

    useEffect(() => {
        if (tabName && tabsContext) {
            tabsContext.toggleErrorExclamationSign(
                    tabName,
                    keys.some(key => errorsBagIsNotEmpty(errors[key]))
            );
        }
    }, [errors, keys, tabName, tabsContext]);

    useEffect(() => {
        if (
                tabsContext &&
                tabName &&
                tabsContext.isVisibleTab(tabName) !== !keys.length &&
                keys.every(key => fields[key].dontChangeTabVisibility !== true)
        ) {
            tabsContext.changeVisibility(tabName, Boolean(keys.length));
        }
    }, [keys.length, tabsContext, tabName, keys, fields]);

    return <React.Suspense fallback={<NowLoadingMessage/>}>
        {keys.map(key => {
            const field = fields[key];
            const fieldName = 'name' in field ? String(field['name']) : key;
            const customField = customFields?.[fieldName];
            const config = customField?.config ? {...field, ...customField?.config} : field;
            const FieldComponent: FormCustomField<Model>['component'] = customField?.component ?? FormField;

            return <FieldComponent
                    key={props.formName + '-' + key + '-field'}
                    type={field.type ?? FormFieldAvailableTypes.TEXT}
                    config={config}
                    model={props.model}
                    modelOptions={props.modelOptions}
                    labelL10NPrefix={props.labelL10NPrefix}
                    formName={props.formName}
                    fieldName={fieldName}
                    fieldLabel={(props.labelL10NPrefix ?? '') + (field.label ?? key)}
                    tabName={tabName}
            />;
        })}
    </React.Suspense>;
}

export function Tabs<Model extends IEmptyModel>(props: WithRequirements<IFormTabConfig<Model, IFormTabFieldConfig>, Model> & {
    context?: FormContext,
}) {
    const defaultTab = props.defaultTab || 'Forms.default-tab';

    const fields = props.fields;
    const formContext = props.context;
    const {canAny} = usePermissions();
    const [tabs, tabsKeys, initialTabsStates, tabsWithoutExclamations] = useMemo(() => {
        const initialTabsStates: Record<string | number, boolean> = {};
        const tabsWithoutExclamations: Record<string | number, boolean> = {};

        const tabs = Object.keys(fields).reduce((res, fieldName) => {
            const field: IFormTabFieldConfig = fields[fieldName as keyof typeof fields];
            if (
                    formContext &&
                    field.context &&
                    formContext !== field.context ||
                    !canAny(field.permissions ?? [])
            ) {
                return res;
            }

            if (field.withoutExclamations === true) {
                tabsWithoutExclamations[field.tab ?? defaultTab] = true;
            }
            initialTabsStates[field.tab ?? defaultTab] = true;

            const tabFields: any = res[field.tab ?? defaultTab] ??= {};
            tabFields[field.label ?? fieldName] = field;

            return res;
        }, {} as Record<string, FieldsProps<Model, IFormTabFieldConfig>>);

        return [tabs, Object.keys(tabs), initialTabsStates, tabsWithoutExclamations];
    }, [canAny, defaultTab, fields, formContext]);

    const [tabsStates, setTabStates] = useState<Record<string | number, boolean>>(initialTabsStates);
    const [badges, setBadges] = useState<Record<string | number, { color?: string, label: string }>>({});
    const [exclamationSigns, setExclamationSigns] = useState<Record<string | number, boolean>>({});
    const [errorsExclamationSigns, setErrorsExclamationSigns] = useState<Record<string | number, boolean>>({});

    const navigate = useNavigate();
    const location = useLocation();
    const locationTab = decodeURIComponent(location.hash.slice(1));
    const activeTab = locationTab && tabsKeys.includes(locationTab) ? locationTab : defaultTab;

    const formTabsContextValues = useMemo<React.ContextType<typeof FormTabsContext>>(() => {
        const isExclamationSignDisplayed = (tabName: string | number): boolean => {
            return (tabName in exclamationSigns) && exclamationSigns[tabName];
        };
        const toggleExclamationSign = (tabName: string | number, state?: boolean | null): void => {
            state ??= !isExclamationSignDisplayed(tabName);
            if (isExclamationSignDisplayed(tabName) === state) {
                return;
            }

            setExclamationSigns(map => {
                if (state) {
                    map[tabName] = true;
                } else {
                    delete map[tabName];
                }

                return {...map};
            });
        };
        const toggleErrorExclamationSign = (tabName: string | number, state?: boolean | null): void => {
            const isDisplayed = (tabName in errorsExclamationSigns) && errorsExclamationSigns[tabName];
            state ??= !isDisplayed;
            if (isDisplayed === state) {
                return;
            }

            setErrorsExclamationSigns(map => {
                if (state) {
                    map[tabName] = true;
                } else {
                    delete map[tabName];
                }

                return {...map};
            });
        };
        const showExclamationSign = (tabName: string | number): void => toggleExclamationSign(tabName, true);
        const hideExclamationSign = (tabName: string | number): void => toggleExclamationSign(tabName, false);

        const getActiveTabName = (location: Location) => {
            const locationTab = decodeURIComponent(location.hash.slice(1));

            return locationTab && tabsKeys.includes(locationTab) ? locationTab : defaultTab;
        };

        return {
            getActiveTabName,
            isActiveTab(location: Location, tabName: string | number): boolean {
                return getActiveTabName(location) === tabName;
            },
            isVisibleTab(tabName: string | number): boolean {
                return !tabsStates.hasOwnProperty(tabName) || tabsStates[tabName];
            },
            changeVisibility(tabName: string | number, newState: boolean): void {
                if (this?.isVisibleTab(tabName) !== newState) {
                    setTabStates(prev => ({...prev, [tabName]: newState}));
                }
            },
            setBadge(tabName: string | number, badge: null | { color?: string, label: string }): void {
                setBadges(prev => {
                    if (!badge) {
                        delete prev[tabName];

                        return {...prev};
                    }

                    return {
                        ...prev,
                        [tabName]: {
                            ...(prev[tabName] ?? {}),
                            ...badge,
                        }
                    };
                });
            },
            isExclamationSignDisplayed,
            toggleExclamationSign,
            toggleErrorExclamationSign,
            hideExclamationSign,
            showExclamationSign
        };
    }, [defaultTab, errorsExclamationSigns, exclamationSigns, tabsKeys, tabsStates]);

    const tabsContentsWrappers = props.customTabsContentWrappers ?? {};
    const tabsControlsWrappers = props.customTabsControlWrappers ?? {};

    const controls = <Nav
            className={classnames({
                'nav-tabs-custom': props.pills !== true && props.vertical !== true,
            })}
            vertical={props.vertical === true}
            justified={props.justified === true}
            pills={props.pills === true || props.vertical === true}
            tabs={props.vertical !== true && props.pills !== true}
    >
        {tabsKeys.map(name => {
            const icon = props?.icons?.[name];
            const badge = badges[name] ?? null;

            const displayExclamation = !tabsWithoutExclamations[name] && (
                    Boolean(exclamationSigns[name]) || Boolean(errorsExclamationSigns[name])
            );
            const TabControlWrapper = tabsControlsWrappers[name] ?? DefaultTabControlWrapper;

            return <NavItem
                    key={props.formName + '-' + name + '-tab-control'}
                    className={classnames({
                        'd-none': (name in tabsStates) && !tabsStates[name],
                        'has-errors': displayExclamation,
                    })}
            >
                <TabControlWrapper
                        formName={props.formName}
                        context={props.context}
                        model={props.model}
                        modelOptions={props.modelOptions}
                >
                    <NavLink
                            href={`#${name}`}
                            active={activeTab === name}
                            onClick={e => {
                                e.preventDefault();

                                navigate({...location, hash: name}, {replace: true});
                            }}
                    >
                        <RenderIf expression={icon}><i className={icon}/></RenderIf>

                        <Translate message={name}/>

                        <RenderIf expression={badge}>
                        <span className={classnames([
                            'badge ms-2',
                            `bg-${activeTab === name ? (badge?.color ?? 'primary') : 'secondary'}`
                        ])}>
                            {badge?.label}
                        </span>
                        </RenderIf>

                        <RenderIf expression={displayExclamation}>
                            <span className="exclamation-sign ms-2">!</span>
                        </RenderIf>
                    </NavLink>
                </TabControlWrapper>
            </NavItem>;
        })}
    </Nav>;

    const content = <TabContent activeTab={activeTab} className={classnames({
        'py-3': props.vertical !== true,
        'mt-3 mt-md-0': props.vertical === true,
    })}>
        {tabsKeys.map(name => {
            const possibleTabNames = [
                name,
                name === 'Forms.default-tab' ? 'default' : null,
                name.replace(/.+\.([^.]+)/guim, '$1')
            ];
            const customTabName = possibleTabNames.find(name => name && (name in tabsContentsWrappers));
            const TabContentWrapper = tabsContentsWrappers[customTabName ?? ''] ?? DefaultTabContentWrapper;

            return <TabPane
                    tabId={name} key={props.formName + '-' + name + '-tab-pane'}
                    className={classnames({'d-none': (name in tabsStates) && !tabsStates[name]})}
            >
                <TabContentWrapper
                        formName={props.formName}
                        context={props.context}
                        model={props.model}
                        modelOptions={props.modelOptions}
                >
                    <Fields
                            model={props.model}
                            context={props.context}
                            labelL10NPrefix={props.labelL10NPrefix}
                            modelOptions={props.modelOptions}
                            formName={props.formName}
                            fields={tabs[name]}
                            customFields={props.customFields}
                            tabName={name}
                    />
                </TabContentWrapper>
            </TabPane>;
        })}
    </TabContent>;

    const wrappersProps = {
        formName: props.formName,
        context: props.context,
        model: props.model,
        modelOptions: props.modelOptions,
    };
    const GlobalContentWrapper = tabsContentsWrappers['global'] ?? DefaultTabContentWrapper;
    const GlobalControlsWrapper = tabsControlsWrappers['global'] ?? DefaultTabControlWrapper;
    const wrappedControls = <GlobalControlsWrapper {...wrappersProps}>{controls}</GlobalControlsWrapper>;
    const wrappedContent = <GlobalContentWrapper {...wrappersProps}>{content}</GlobalContentWrapper>;

    return <FormTabsContext.Provider value={formTabsContextValues}>
        {props.vertical ? <Row>
            <Col md={props.verticalTabsColumnSize ?? 3}>{wrappedControls}</Col>
            <Col>{wrappedContent}</Col>
        </Row> : <>
            {wrappedControls}
            {wrappedContent}
        </>}
    </FormTabsContext.Provider>;
}

export default function FormFields<Model extends IEmptyModel>(props: FormFieldsProps<Model>) {
    return <>
        {props.config.fields ? (
                <Fields
                        model={props.model}
                        context={props.context}
                        fields={props.config.fields as FieldsProps<Model, IFormFieldConfig>}
                        modelOptions={props.modelOptions}
                        labelL10NPrefix={props.config.fieldsNameL10NPrefix}
                        formName={props.formName}
                        customFields={props.customFields}
                        customTabsContentWrappers={props.customTabsContentWrappers}
                        customTabsControlWrappers={props.customTabsControlWrappers}
                />
        ) : null}

        {props.config.tabs ? (
                <Tabs
                        model={props.model}
                        context={props.context}
                        {...(props.config.tabs as IFormTabConfig<Model, IFormTabFieldConfig>)}
                        modelOptions={props.modelOptions}
                        labelL10NPrefix={props.config.fieldsNameL10NPrefix}
                        formName={props.formName}
                        customFields={props.customFields}
                        customTabsContentWrappers={props.customTabsContentWrappers}
                        customTabsControlWrappers={props.customTabsControlWrappers}
                />
        ) : null}
    </>;
}
